import {
    Component,
    Input,
    OnDestroy,
    OnChanges,
    OnInit,
    EventEmitter,
    Output,
    Injector,
} from '@angular/core';
import { Subscription } from 'rxjs';

import {
    ColumnSelect,
    ColumnSelectLabel
} from '@common/facet';
import { DroppableEvent } from '../../../../common/droppable-event';
import {
    uniqueArrayFromPropertyPath,
} from '../../../../common/util';

import { JobPharmaDetailService } from '../../services/job-pharma-detail.service';
import { LoggingService } from '../../../../services/logging.service';
import { ProtocolService } from '../../../../protocol/protocol.service';
import { TaskType } from '../../../../tasks/models';
import { TaskService } from '../../../../tasks/task.service';
import {
    ViewAddProtocolComponentService
} from '../../../../tasks/add-task/view-add-protocol-component.service';
import {
    ViewAddTaskComponentService
} from '../../../../tasks/add-task/view-add-task-component.service';
import { VocabularyService } from '../../../../vocabularies/vocabulary.service';
import { WorkflowService } from '../../../../workflow/services/workflow.service';
import { SaveChangesService } from '../../../../services/save-changes.service';

import { DataManagerService } from "../../../../services/data-manager.service";
import { WebApiService } from "../../../../services/web-api.service";
import { DialogService, isActionClicked } from '@common/dialog/deprecated';

import { ProtocolDateCalculator } from '../../../../tasks/tables/protocol-date-calculator';
import { EntityQuery, EntityState, Predicate, QueryOptions } from 'breeze-client';
import { TaskModalAction, TaskModalActionType, TaskModalComponent } from '../../../../tasks/task-modal.component';
import { IFacet } from '../../../../common/facet';
import type { Animal, Cohort, Entity, Job, ExtendedJob, ProtocolInstance, TaskCohort, TaskInstance, TaskMaterial, TaskJob, Material, TaskPlaceholder } from '@common/types';
import { JobPharmaTableService } from '../../services/job-pharma-table.service';
import { ExtendedTaskInstance } from "../../models/extended-task-instance";
import { pluralize } from '@common/util/pluralize';
import { JobPharmaTasksListTableService } from './job-pharma-tasks-list-table.service';
import { JobPharmaCoreService, JobPharmaDataAccessService, TabRefreshEvent } from '../../services';
import { applyMixins, Base, CanUnsubscribeMixin, mixinUnsubscribe } from '@common/mixins';
import { TaskListTableColumnsHelper } from './task-list-table-columns-helper';
import { VisibleColumns } from '@common/util/table-columns/base-table-columns-helper';
import { CanLoadMixin, mixinLoadable } from '@common/mixins/loadable.mixin';
import { debounceTime, filter, takeLast } from 'rxjs/operators';
import { DataContextService } from '@services/data-context.service';

const Mixins: CanUnsubscribeMixin & CanLoadMixin & typeof Base = applyMixins(mixinUnsubscribe, mixinLoadable);

// Status counts for groups
class StatusCount {
    endState = 0;
    total = 0;

    get done(): boolean {
        return (this.total > 0) && (this.endState === this.total);
    }
}

export class SaveRecordsOverlayEvent {
    state: boolean;
    message?: string;
}

@Component({
    selector: 'job-pharma-tasks-list-table',
    templateUrl: './job-pharma-tasks-list-table.component.html',
    providers: [
        TaskListTableColumnsHelper
    ]
})
export class JobPharmaTasksListTableComponent extends Mixins implements OnChanges, OnDestroy, OnInit {
    @Input() readonly: boolean;
    @Input() job: Entity<Job & ExtendedJob>;
    @Input() numItemsExpanded: number;
    @Input() isCRO: boolean;
    @Input() isCRL: boolean;
    @Input() isGLP: boolean;
    @Input() isStudyDirector: boolean;
    @Input() facet: IFacet;

    // Data Output Events
    @Output() materialAdd: EventEmitter<any> = new EventEmitter<any>();
    @Output() busy: EventEmitter<SaveRecordsOverlayEvent> = new EventEmitter<SaveRecordsOverlayEvent>();

    loading = false;
    loadingMessage = "Loading";

    taskPage = 1;
    pageSize = 50;
    totalGroupTaskInstances = 0;
    // Tasks to show in the table
    tasks: ExtendedTaskInstance[] = [];

    // Column selections
    columnSelect: ColumnSelect = {
        model: [],
        labels: [],
    };

    // Visible columns
    visibleColumns: VisibleColumns;

    // Are all the rows selected?
    allSelected = false;
    allLocked = false;

    selectedTasks: ExtendedTaskInstance[] = [];

    defaultTaskStatusKey: any;

    readonly COMPONENT_LOG_TAG = 'job-pharma-tasks-list-table';
    readonly SAVING_MESSAGE = 'Saving. It may take a minute or longer to save a large number of records.';
    readonly tabset = 'tasks';
    readonly tab = 'list';

    constructor(
        injector: Injector,
        private jobPharmaDetailService: JobPharmaDetailService,
        private jobPharmaTableService: JobPharmaTableService,
        private taskListTableService: JobPharmaTasksListTableService,
        private jobPharmaCoreService: JobPharmaCoreService,
        private jobPharmaDataAccessService: JobPharmaDataAccessService,
        private loggingService: LoggingService,
        public protocolService: ProtocolService,
        public taskService: TaskService,
        private vocabularyService: VocabularyService,
        private viewAddProtocolComponentService: ViewAddProtocolComponentService,
        private viewAddTaskComponentService: ViewAddTaskComponentService,
        private workflowService: WorkflowService,
        private saveChangesService: SaveChangesService,
        private dataManager: DataManagerService,
        private webApiService: WebApiService,
        private dialogService: DialogService,
        private taskListTableColumnsHelper: TaskListTableColumnsHelper,
        private dataContextService: DataContextService
    ) {
        super(injector)
    }

    async ngOnInit() {
        this.setLoading(true)
        this.initChangeDetection();
        this.initColumnSelect();
        await this.initialize();
        this.initTabActions();
        this.setLoading(false);
    }

    ngOnChanges(changes: any) {
        if (changes.job && !changes.job.firstChange) {
            this.initJob();
        }
    }

    ngOnDestroy() {
        // Clear all the subscriptions
        super.ngOnDestroy();
    }


    /**
     * Watch for external changes
     */
    initChangeDetection() {
        // Watch for changes to job.TaskJob
        this.subscription = (
            this.jobPharmaDetailService.jobTasksChanged$.pipe(debounceTime(100)).subscribe(() => {
                this.initJob();
            })
        );

        this.subscription = (
            this.saveChangesService.saveSuccessful$.subscribe(() => {
                this.initStatusCounts(this.tasks);
            })
        )
    }

    /**
     * Watch for event between the tabs
     */
    initTabActions() {
        // Listen for calls to refresh the view
        this.subscription = (
            this.jobPharmaDetailService.tabRefresh$.pipe(
                    filter((e: TabRefreshEvent) => e.tabset === 'tasks' && e.tab === 'list'), 
                    debounceTime(100))
                .subscribe((event) => {
                    if (event.tabset === 'tasks' && event.tab === 'list') {
                        this.initJob();
                    }
            })
        );
    }

    /**
     * Initialize the column selections
     */
    initColumnSelect() {

        const {
            columnSelect,
            visibleColumns,
            updateVisibleColumns
        } = this.taskListTableColumnsHelper.create();
        this.visibleColumns = visibleColumns;

        this.subscribe(
            this.jobPharmaDetailService.registerColumnSelect(
                this.tabset,
                this.tab,
                columnSelect,
                () => { updateVisibleColumns(); }
            )
        );

        updateVisibleColumns();
    }

    async initialize(): Promise<any> {
        await this.getCVs();
        await this.initJob();
    }

    async getCVs(): Promise<any> {
        await Promise.all([
            this.vocabularyService.ensureCVLoaded('cv_TaskStatuses')
        ]);
        const preferLocal = true;
        const status = await this.vocabularyService.getCVDefault(
            'cv_TaskStatuses', preferLocal
        );
        if (status) {
            this.defaultTaskStatusKey = status.C_TaskStatus_key;
        }
    }

    /**
     * Initialize the Job-related values.
     */
    async initJob(): Promise<void> {
        this.setLoading(true);

        const taskExpands = [
            // Need the status IsEndState when counting
            'cv_TaskStatus',
            // For Animals
            'TaskCohort.Cohort',
            'TaskMaterial.Material.Animal',
            'Job.TaskJob.TaskInstance.TaskCohort',
            // For Samples
            'SampleGroup',
            'SampleGroup.SampleGroupSourceMaterial',
            'MemberTaskInstance.TaskMaterial',
            // For Placeholder
            'TaskPlaceholder',
            'TaskPlaceholder.Placeholder.JobCohort',
            'TaskPlaceholder.TaskPlaceholderInput',
            // For Inputs
            'TaskInput.Input.cv_DataType',
            'TaskInput.Input.cv_DosingTable',
            'TaskInput.Input.cv_JobCharacteristicType'
        ];

        try {
            this.totalGroupTaskInstances = await this.jobPharmaDataAccessService.getGroupTaskInstancesCount(this.job.C_Job_key);
            
            const groupTaskJobs = await this.loadGroupTaskJobs(this.taskPage, this.pageSize);
            const tasks = await this.jobPharmaDetailService.initTasks(this.job, taskExpands, groupTaskJobs.filter(tj => tj.entityAspect.entityState !== EntityState.Deleted));
            await this.initStatusCounts(tasks);
            this.tasks = this.initRows(tasks);
            this.isSelectedChanged();

            this.checkIfAllLocked();
        } finally {
            this.setLoading(false);
        }
    }

    
    /**
     * Loads all the group task jobs for a given page, for a given page size.
     * @param page 
     * @param pageSize
     */
    async loadGroupTaskJobs(page: number, pageSize: number): Promise<Entity<TaskJob>[]> {
        const groupTaskInstanceKeys = await this.jobPharmaDataAccessService.getPagedAndSortedGroupTaskInstanceKeys(this.job.C_Job_key, page, pageSize);
        return await this.jobPharmaDataAccessService.getGroupTaskJobs(groupTaskInstanceKeys);
    }

    /**
     * Count the total and completed tasks in each group
     *
     * @param tasks tasks that will be displayed
     */
    async initStatusCounts(tasks: ExtendedTaskInstance[]) {

        const query = EntityQuery.from('TaskInstances')
            .where(Predicate.create("C_GroupTaskInstance_key", "in", tasks.map(t => t.C_TaskInstance_key)));
        query.queryOptions = new QueryOptions();
        query.queryOptions.includeDeleted = true;

        const response = await this.dataManager.executeQueryLocal(query);
        const deletedTasks = response.results.filter(ti => ti.entityAspect.entityState.isDeleted()) as Entity<TaskInstance>[];
        const deletedTasksMap = new Map<number, TaskInstance[]>();
        deletedTasks.forEach((ti) => {
            if (deletedTasksMap.has(ti.C_GroupTaskInstance_key)) {
                deletedTasksMap.get(ti.C_GroupTaskInstance_key).push(ti);
            } else {
                deletedTasksMap.set(ti.C_GroupTaskInstance_key, [ti]);
            }
        });

        const statusCounts = await this.jobPharmaCoreService.getTaskStatusCounts(this.job.C_Job_key);
        tasks.forEach((task) => {
            task.statusCount = new StatusCount();
            // Account for deleted tasks intances which have not been saved
            if (deletedTasksMap.get(task.C_TaskInstance_key)) {
                task.statusCount.total = statusCounts[task.C_TaskInstance_key]?.TotalCount - deletedTasksMap.get(task.C_TaskInstance_key).length;
                task.statusCount.endState = statusCounts[task.C_TaskInstance_key]?.CompletedCount;
            } else if (!statusCounts[task.C_TaskInstance_key]){
                // If task has been added to job but not yet saved, it will not appear in the statusCounts map, and both total and endstate counts should be 0
                task.statusCount.total = 0;
                task.statusCount.endState = 0;
            } 
            else {
                task.statusCount.total = statusCounts[task.C_TaskInstance_key]?.TotalCount;
                task.statusCount.endState = statusCounts[task.C_TaskInstance_key]?.CompletedCount;
            }
        });

    }

    private initRows(tasks: any[]): any[] {
        for (const task of tasks) {
            if (!task.SampleGroup || (task.SampleGroup.length === 0)) {
                task.SampleGroupTypes = '';
                task.numSources = null;
                task.numSourceAnimals = null;
                task.numSourceSamples = null;
                task.numSourceAnimalPlaceholders = null;
                continue;
            }

            // Count the sources in the task
            let numSourceAnimals = 0;
            let numSourceSamples = 0;
            let numSourceAnimalPlaceholders = 0;

            task.SampleGroupTypes = '';
            for (const sampleGroup of task.SampleGroup) {
                if (sampleGroup.cv_SampleType) {
                    task.SampleGroupTypes += sampleGroup.cv_SampleType.SampleType + ', ';
                }
                numSourceAnimals += sampleGroup.SampleGroupSourceMaterial.filter((sm: any) => sm.Material && sm.Material.Animal !== null).length;
                numSourceSamples += sampleGroup.SampleGroupSourceMaterial.filter((sm: any) => sm.Material && sm.Material.Sample !== null).length;
                numSourceAnimalPlaceholders += sampleGroup.SampleGroupSourceMaterial.filter((sm: any) => sm.AnimalPlaceholder && !sm.AnimalPlaceholder.Material).length;
            }
            task.SampleGroupTypes = task.SampleGroupTypes
                .substring(0, task.SampleGroupTypes.length - 2);
            task.numSourceAnimals = numSourceAnimals;
            task.numSourceSamples = numSourceSamples;
            task.numSourceAnimalPlaceholders = numSourceAnimalPlaceholders;
            task.numSources = numSourceAnimals + numSourceSamples + numSourceAnimalPlaceholders;
        }
        return tasks;
    }

    /**
     * The Select/Clear All button was clicked
     */
    allSelectedChanged() {
        // Select or unselect all the rows
        if (this.tasks) {
            for (const task of this.tasks) {
                task.isSelected = this.allSelected;
            }
        }
        this.isSelectedChanged();
    }
    
    async allLockedChanged() {
        // locked or unlocked all the rows
        this.setLoading(true);
        try {
            const groupTaskInstances = (await this.loadGroupTaskJobs(0, this.totalGroupTaskInstances))
            .filter((gtj: Entity<TaskJob>) => gtj.entityAspect.entityState !== EntityState.Deleted)
            .map((gtj: Entity<TaskJob>) => gtj.TaskInstance);

            
            for (const task of groupTaskInstances) {
                task.IsLocked = this.allLocked;
            }
        }
        finally {
            this.setLoading(false)
        }
    }

    /**
     * A row selection checkbox was clicked.
     */
    isSelectedChanged(): void {
        // Check if all the rows are selected
        this.allSelected = this.tasks.every((task) => task.isSelected) && this.tasks.length > 0;
        // update selected tasks list.
        this.selectedTasks = [... new Set([...this.selectedTasks, ...this.tasks])].filter((task) => task.isSelected
            && task.entityAspect.entityState != EntityState.Deleted
        );
    }

    checkIfAllLocked(): void {
        // Check if all the rows are locked
        this.allLocked = this.tasks.every((task) => task.IsLocked);
    }

    onLockedChanged(task: TaskInstance): void {
        // lock all member task instances
        this.job.TaskJob.forEach(tj => {
            if (tj.TaskInstance.C_GroupTaskInstance_key === task.C_TaskInstance_key) {
                tj.TaskInstance.IsLocked = task.IsLocked;
            }
        })
        this.checkIfAllLocked()
    }


    /**
     * Add tasks for a Protocol selected through the modal.
     *
     * Note: The add-protocol component provides the initial values for the
     * TaskInstances.
     */
    async addProtocolViaModal() {
        // FORCE SAVE BEFORE ADD PROTOCOL
        if (this.canSave) {
            let updatePlaceholders = false;
            if (this.job.JobID === null || this.job.JobID.length === 0) {
                updatePlaceholders = true;
            }
            this.saveChangesService.saveChanges(this.COMPONENT_LOG_TAG).then(() => {
                if (updatePlaceholders) {
                    this.jobPharmaDetailService.updatePlaceholderNames(this.job);
                }
            });
        }

        // Show the Add Protocol modal
        const protocolKey = await this.viewAddProtocolComponentService.openComponent(TaskType.Job);
        if (!protocolKey) {
            return;
        }
        const protocol = await this.protocolService.getProtocol(protocolKey);
        try {
            this.busy.emit({ state: true, message: this.loadingMessage });
            await this.jobPharmaDetailService.addProtocols(this.job, [protocol]);
        } finally {
            this.busy.emit({ state: false, message: this.loadingMessage });
        }
    }

    /**
     * Add tasks selected through the modal.
     *
     * Note: The add-task modal returns the initial values for the selected
     * tasks.
     */
    async addTasksViaModal() {
        // FORCE SAVE BEFORE ADD TASK
        if (this.canSave) {
            let updatePlaceholders = false;
            if (this.job.JobID === null || this.job.JobID.length === 0) {
                updatePlaceholders = true;
            }
            this.saveChangesService.saveChanges(this.COMPONENT_LOG_TAG).then(() => {
                if (updatePlaceholders) {
                    this.jobPharmaDetailService.updatePlaceholderNames(this.job);
                }
            });
        }

        const taskValues = await this.viewAddTaskComponentService.openComponent(TaskType.Job);
        try {
            this.busy.emit({ state: true, message: this.loadingMessage });
            await this.jobPharmaDetailService.addTasks(this.job, taskValues);
            await this.dataContextService.saveEntities([
                "TaskInstance",
                "TaskJob",
                "TaskInput",
                "SampleGroup"
            ]);
            this.initJob();
        } finally {
            this.busy.emit({ state: false, message: this.loadingMessage });
        }
    }

    /**
     * Handle drop events
     */
    async onDrop(event: DroppableEvent): Promise<any> {
        try {
            this.busy.emit({ state: true, message: this.loadingMessage });
            await this.jobPharmaTableService.onDropToJob(this.job);
            await this.dataContextService.saveEntities([
                "TaskInstance",
                "TaskJob",
                "TaskInput",
                "SampleGroup"
            ]);
            this.initJob();
        } finally {
            this.busy.emit({ state: false, message: this.loadingMessage });
        }
    }

    /**
     * Create a new ProtocolInstance
     */
    addProtocolInstance(protocol: any) {
        // Get all the existing task instances to help make the next alias
        let allTaskInstances = uniqueArrayFromPropertyPath(
            this.job, 'TaskJob.TaskInstance'
        );
        // Filter to have only parent task instances
        allTaskInstances = allTaskInstances.filter((task) => {
            return task && !task.C_GroupTaskInstance_key;
        });

        // Get protocol instances to help make the next alias
        const allProtocolInstances = uniqueArrayFromPropertyPath(
            allTaskInstances, 'ProtocolInstance'
        );

        // Create a new instance
        return this.taskService.createProtocolInstance({
            C_Protocol_key: protocol.C_Protocol_key,
            ProtocolAlias: this.taskService.generateProtocolAlias(
                allProtocolInstances, protocol
            ),
        });
    }

    /**
     * Delete task instance from job
     * @param task
     */
    async removeTask(task: ExtendedTaskInstance): Promise<void> {
        this.busy.emit({ state: true, message: this.loadingMessage });

        await this.dataManager.lazyLoadExpands([ task ], ["MemberTaskInstance.TaskJob"])

        while (task.MemberTaskInstance.length > 0) {
            this.workflowService.deleteTask(task.MemberTaskInstance[0]);
        }

        this.workflowService.deleteTask(task);
        this.isSelectedChanged();

        this.jobPharmaDetailService.tabRefresh("tasks", "list");
        this.loggingService.logSuccess('Task Deleted', null, 'jobs', true);
        this.busy.emit({ state: false, message: this.loadingMessage });
    }

    /**
     * Deletes task instances that are selected from a job
     */
    removeTasks() {
        // Count selected tasks
        const isSelectedTasksLength = this.selectedTasks.length;
        const selectedTasksAmount = isSelectedTasksLength || this.tasks.length;
        // Count eligible tasks
        const eligibleTasks = this.selectedTasks.filter((task) => {
            return this.canRemoveTask(task);
        }).length;

        const tasksInWorkflowMessage = eligibleTasks !== selectedTasksAmount ? 'The remaining tasks have associated workflow data and/or sample groups and cannot be deleted.' : '';
        const message = `${eligibleTasks} ${pluralize(eligibleTasks, 'task')} out of ${selectedTasksAmount} ${pluralize(selectedTasksAmount, 'task')} can be deleted. ${tasksInWorkflowMessage} Do you wish to continue?`;
        const options = {
            title: 'Delete Tasks',
            bodyText: message,
        };

        this.dialogService.confirmDelete(options).then(async (result) => {
            if (result) {
                this.busy.emit({state: true, message: this.loadingMessage});
                await this.dataManager.lazyLoadExpands(this.selectedTasks, ["TaskJob", "MemberTaskInstance.TaskJob"])

                this.selectedTasks = this.selectedTasks.filter((task) => {
                    if (this.canRemoveTask(task) && task.SampleGroup?.length === 0) {
                        while (task.MemberTaskInstance?.length > 0) {
                            this.workflowService.deleteTask(task.MemberTaskInstance[0]);
                        }

                        this.workflowService.deleteTask(task);

                        return false;
                    }

                    return true;
                });

                await this.saveChangesService.saveChanges(this.COMPONENT_LOG_TAG);

                this.jobPharmaDetailService.tabRefresh("tasks", "list");
                this.loggingService.logSuccess('Eligible Tasks Deleted', null, 'jobs', true);
                this.busy.emit({ state: false, message: this.loadingMessage });
            }
        }).catch((error: any) => {
            console.error(error);
        });
    }

    get canSave(): boolean {
        return this.saveChangesService.hasChanges && !this.saveChangesService.saving;
    }

    async onDropToProtocolInstance(protocolInstance: ProtocolInstance, event: DroppableEvent): Promise<any> {
        try {
            this.busy.emit({ state: true, message: this.loadingMessage });
            return await this.jobPharmaTableService.onDropToProtocolInstance(this.job, protocolInstance, event);
        } finally {
            this.busy.emit({ state: false, message: this.loadingMessage });
        }
    }

    async onDropToTask(task: TaskInstance, event: DroppableEvent): Promise<any> {
        try {
            this.busy.emit({ state: true, message: this.loadingMessage });
            return await this.jobPharmaTableService.onDropToTask(this.job, task, event);
        } finally {
            this.busy.emit({ state: false, message: this.loadingMessage });
        }
    }

    /**
     * Check if cohort has already been added to a protocol
     */
    checkForDupCohorts(protocolInstance: any, entities: any[]): any {
        let taskCohorts: any[];
        let cohorts: any[];
        let duplicates: any[];

        for (const task of protocolInstance.TaskInstance) {
            taskCohorts = task.TaskCohort;
            cohorts = taskCohorts.map((taskCohort) => taskCohort.Cohort);
            duplicates = entities.filter((entity) => {
                return cohorts.includes(entity);
            });
        }

        return duplicates;
    }

    async onRemoveAnimal(task: Entity<TaskInstance>, animal: Entity<Animal>) {
        try {
            this.busy.emit({ state: true, message: this.loadingMessage });
            await this.jobPharmaDetailService.removeAnimal(this.job, task, animal);
        } finally {
            this.busy.emit({ state: false, message: this.loadingMessage });
        }
    }

    async onRemoveCohort(task: Entity<TaskInstance>, cohort: Entity<Cohort>, taskCohort: Entity<TaskCohort>) {
        try {
            this.busy.emit({ state: true, message: this.loadingMessage });
            await this.taskListTableService.removeCohortFromTask(this.job, taskCohort);
        } finally {
            this.busy.emit({ state: false, message: this.loadingMessage });
        }
    }

    async changeTaskPage(newPage: number) {
        this.taskPage = newPage;
        await this.initJob();
        this.isSelectedChanged();
    }

    /**
     * Calculate the DateDue based on Material field(s)
     *
     * @param taskInstanceKey
     */
    calculateMaterialDueDatesFromTaskKey(taskInstance: any) {
        const protocolDateCalculator = new ProtocolDateCalculator();
        const allTasks = this.getAllTaskInstances();
        const protocolInstanceTasks = this.getTasksInSameProtocol(taskInstance, allTasks);
        protocolDateCalculator.scheduleMaterialDueDates(protocolInstanceTasks, taskInstance);
    }

    getTaskInstanceByKey(taskInstanceKey: number): any[] {
        const allTasks = this.getAllTaskInstances();
        return allTasks.find((item) => {
            return item.C_TaskInstance_key === taskInstanceKey;
        });
    }

    getTaskInstanceByGroupKey(taskInstanceKey: number): any[] {
        const allTasks = this.getAllTaskInstances();
        return allTasks.find((item) => {
            return item.C_TaskInstance_key === taskInstanceKey;
        });
    }

    getAllTaskInstances(): any[] {
        return uniqueArrayFromPropertyPath(this.tasks, 'TaskInstance');
    }

    getTasksInSameProtocol(taskInstance: any, allTasks: any[]): any[] {
        return allTasks.filter((task) => {
            return task.C_ProtocolInstance_key === taskInstance.C_ProtocolInstance_key;
        });
    }

    async onRemoveSample(taskMaterial: TaskMaterial) {
        try {
            this.busy.emit({ state: true, message: this.loadingMessage });
            await this.jobPharmaDetailService.removeSample(this.job, taskMaterial);
            this.calculateMaterialDueDatesFromTaskKey(taskMaterial.C_TaskInstance_key);
        } finally {
            this.busy.emit({ state: false, message: this.loadingMessage });
        }
    }



    getTaskDeleteTitle(task: any) {
        if (this.hasSampleGroups(task)) {
            return 'First delete associated sample group(s)';
        } else {
            return 'Remove Task';
        }
    }

    hasSampleGroups(task: TaskInstance): boolean {
        return task.SampleGroup.length > 0;
    }

    onRemovePlaceholder(taskPlaceholder: TaskPlaceholder, task: TaskInstance) {
        this.jobPharmaDetailService.removePlaceholder(taskPlaceholder, task);
    }

    onRemoveAnimalPlaceholder(taskPlaceholder: TaskPlaceholder, task: TaskInstance) {
        this.jobPharmaDetailService.removeAnimalPlaceholder(taskPlaceholder, task);
    }

    taskDontHaveMatchingCohort(placeholder: any, taskCohort: any[]) {
        return this.jobPharmaDetailService.taskDontHaveMatchingCohort(placeholder, taskCohort);
    }

    taskDontHaveMatchingAnimal(placeholder: any, taskMaterial: any[]) {
        return this.jobPharmaDetailService.taskDontHaveMatchingAnimal(placeholder, taskMaterial);
    }

    canRemoveAnyTask(): boolean {
        if (this.selectedTasks.length === 0) {
            return false;
        }
        for (const task of this.tasks) {
            if (this.canRemoveTask(task)) {
                return true;
            }
        }
        return false;
    }

    canRemoveTask(task: any): boolean {
        return !this.readonly &&
            !task.IsLocked &&
            !task.DateComplete &&
            task.statusCount?.endState === 0 &&
            !this.hasSampleGroups(task) &&
            (task.TaskOutputSet && task.TaskOutputSet.length === 0) &&
            (!task.ProtocolInstance || task.ProtocolInstance.UsedInProtocol !== true) &&
            !task.cv_TaskStatus?.IsEndState;
    }

    async editTasksViaModal(task: any): Promise<void> {
        try {
            this.loadingMessage = 'Loading';
            this.busy.emit({ state: true, message: this.loadingMessage });
            const expands = [
                'WorkflowTask.Input.cv_DataType',
                'WorkflowTask.Input.cv_DosingTable',
                'WorkflowTask.Input.EnumerationClass',
                'WorkflowTask.Input.cv_JobCharacteristicType',
                'WorkflowTask.Output.cv_DataType',
                'WorkflowTask.Output.EnumerationClass',
                'WorkflowTask.Output.OutputFlag'
            ];
            await this.dataManager.ensureRelationships([task], expands);

            const customizeComponent = (component: TaskModalComponent) => {
                component.task = task.WorkflowTask;
                component.taskAlias = task.TaskAlias;
                component.taskAlias = task.TaskAlias;
                component.facet = this.facet;
                component.getTotalTasks = () => this.getTotalTasks(task.C_TaskInstance_key, this.job.C_Job_key);
            };
            const action = await this.dialogService.openComponent<TaskModalAction>(TaskModalComponent, {
                customizeComponent,
                systemOptions: {
                    backdrop: 'static',
                    keyboard: false,
                },
            });
            if (!isActionClicked(action)) {
                return;
            }

            const { type, task: editedTask } = action.result;

            await this.saveChangesService.saveChanges(this.COMPONENT_LOG_TAG);
            const isUpdate = type === TaskModalActionType.ApplyToTask;
            await this.applyTask(task.C_TaskInstance_key, this.job.C_Job_key, editedTask.C_WorkflowTask_key, isUpdate);
            task.TaskInput.forEach((ti: any) => {
                this.dataManager.detachEntity(ti);
            });
            return await this.initialize(); // refresh data
        } catch (error) {
            console.error(error);
        } finally {
            this.loadingMessage = '';
            this.busy.emit({ state: false, message: this.loadingMessage });
        }
    }

    getTotalTasks(taskInstanceKey: number, jobKey: number): Promise<any> {
        this.loadingMessage = 'Loading';
        this.busy.emit({ state: true, message: this.loadingMessage });
        const apiUrl = 'api/jobdata/numberOfTasksToEdit?taskInstanceKey=' + taskInstanceKey + '&jobKey=' + jobKey;

        return this.webApiService
            .callApi(apiUrl)
            .then((result) => {
                this.loadingMessage = "";
                this.busy.emit({ state: false, message: this.loadingMessage });
                return result.data;
            })
            .catch((error: any) => {
                console.error(error);
                this.loadingMessage = "";
                this.busy.emit({ state: false, message: this.loadingMessage });
            });
    }

    applyTask(
        taskKey: number,
        jobKey: number,
        workflowKey: number,
        update: boolean
    ): Promise<any> {
        this.loadingMessage = this.SAVING_MESSAGE;
        this.busy.emit({ state: true, message: this.loadingMessage });

        const requestBody = {
            C_TaskInstance_key: taskKey,
            C_WorkflowTask_key: workflowKey,
            C_Job_key: jobKey,
            UpdateThisInstanceOnly: update
        };

        return this.webApiService
            .postApi("api/jobdata/editTaskDefinition", requestBody)
            .then((result) => {
                this.loadingMessage = "";
                this.jobPharmaDetailService.tabRefresh('job', 'main');
                this.busy.emit({ state: false, message: this.loadingMessage });
                return result.data;
            })
            .catch((error: any) => {
                console.error(error);
                this.loadingMessage = "";
                this.busy.emit({ state: false, message: this.loadingMessage });
            });
    }
}

